import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, TOKEN_KEY } from "@/lib/api";

type User = { id: string; login: string; name: string; role: string };
type Ctx = {
  user: User | null;
  loading: boolean;
  login: (login: string, password: string) => Promise<User>;
  logout: (reason?: string) => void;
};

const AuthContext = createContext<Ctx>(null as any);
const LAST_KEY = "nms_last_activity";
const LIMIT_KEY = "nms_inactivity_min";

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const timer = useRef<any>(null);

  const logout = useCallback((reason?: string) => {
    if (localStorage.getItem(TOKEN_KEY)) api.post("/auth/logout").catch(() => {});
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    if (reason) toast.info(reason);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    const limit = Number(localStorage.getItem(LIMIT_KEY) || 15) * 60000;
    const last = Number(localStorage.getItem(LAST_KEY) || 0);
    if (!token || (last && Date.now() - last > limit)) {
      localStorage.removeItem(TOKEN_KEY);
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        localStorage.setItem(LIMIT_KEY, String(r.data.inactivity_minutes));
        setUser(r.data.user);
      })
      .catch(() => localStorage.removeItem(TOKEN_KEY))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const onForced = () => setUser(null);
    window.addEventListener("nms-logout", onForced);
    return () => window.removeEventListener("nms-logout", onForced);
  }, []);

  useEffect(() => {
    if (!user) return;
    const limit = Number(localStorage.getItem(LIMIT_KEY) || 15) * 60000;
    const reset = () => {
      localStorage.setItem(LAST_KEY, String(Date.now()));
      clearTimeout(timer.current);
      timer.current = setTimeout(() => logout("Déconnecté après inactivité"), limit);
    };
    const events = ["mousemove", "keydown", "touchstart", "click", "scroll"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(timer.current);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [user, logout]);

  const login = async (loginId: string, password: string) => {
    const r = await api.post("/auth/login", { login: loginId, password });
    localStorage.setItem(TOKEN_KEY, r.data.token);
    localStorage.setItem(LIMIT_KEY, String(r.data.inactivity_minutes));
    localStorage.setItem(LAST_KEY, String(Date.now()));
    setUser(r.data.user);
    return r.data.user;
  };

  return <AuthContext.Provider value={{ user, loading, login, logout }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
